"use client"

import { Chart, useChart } from "@chakra-ui/charts"
import { Box, Flex, Heading, Text } from "@chakra-ui/react"
import { Area, AreaChart, CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis, ResponsiveContainer, ReferenceArea } from "recharts"
import { DateTime } from "timezonecomplete"
import { toUserTZ, IANATimeZone } from "@hydrologiq/common/helpers/date"

import { Graph, Timeseries } from "@hydrologiq/schemas/1.0.0/installationPage"
import { Menu, Badge } from "@chakra-ui/react"
import { useState, useEffect, useMemo, type ReactNode } from "react"
import { Link } from "@chakra-ui/react"
import { Switch } from "../../../../chakra/Switch"
import { getCategoryColor, OVERLAY_LINE_COLOR, type GraphCategory } from "./graphColors"

/**
 * Recharts adds each right Y-axis `width` to `offset.right` on top of `margin.right`
 * (see recharts `calculateOffset`). Default YAxis width is 60px.
 * - Charts with a secondary right axis: use base margin only (axis supplies the rest).
 * - Single-axis charts: pad by the same width so plot area matches Battery / dual-axis charts.
 */
export const TIMESCALE_CHART_MARGIN_RIGHT_BASE = 60
export const TIMESCALE_SECONDARY_RIGHT_YAXIS_WIDTH_PX = 60

export function timescaleChartMarginRight(hasSecondaryYAxis: boolean): number {
  return (
    TIMESCALE_CHART_MARGIN_RIGHT_BASE +
    (hasSecondaryYAxis ? 0 : TIMESCALE_SECONDARY_RIGHT_YAXIS_WIDTH_PX)
  )
}

/**
 * X-axis rules shared by every monitoring chart (line, stacked, grouped, battery,
 * phase balance), so graphs on one page span and label the same period alike.
 */

/** Snap to the tick positions so the plot ends at the last tick label; else the chart's own range. */
export function timescaleTimeDomain(
  xAxisTicksMs: number[] | undefined,
  start: DateTime | undefined,
  end: DateTime | undefined
): [number, number] | undefined {
  if (xAxisTicksMs && xAxisTicksMs.length >= 2) {
    return [xAxisTicksMs[0], xAxisTicksMs[xAxisTicksMs.length - 1]]
  }
  if (start && end) return [toUserTZ(start).unixUtcMillis(), toUserTZ(end).unixUtcMillis()]
  return undefined
}

/** Length of the charted period in ms (0 when the range is unknown). */
export function timescaleSpanMs(start: DateTime | undefined, end: DateTime | undefined): number {
  return start && end ? Math.abs(toUserTZ(end).unixUtcMillis() - toUserTZ(start).unixUtcMillis()) : 0
}

/** Time of day within a day, weekday, date and time beyond it, in the user's time zone. */
export function timescaleTickFormatter(spanMs: number): (ms: number) => string {
  const tz = IANATimeZone()
  const multiDay = spanMs > 24 * 60 * 60 * 1000
  return (ms: number) => {
    const d = new Date(ms)
    if (multiDay) {
      return d.toLocaleString([], {
        timeZone: tz,
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    }
    return d.toLocaleTimeString([], { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false })
  }
}

/** Minimum gap between tick labels when the parent gave no explicit tick positions. */
export function timescaleMinTickGap(xAxisTickInterval: number | undefined, rows: ReadonlyArray<unknown>): number {
  if (xAxisTickInterval !== undefined && rows.length > 1) {
    const span = Math.abs(
      (rows[rows.length - 1] as { timeMs: number }).timeMs - (rows[0] as { timeMs: number }).timeMs
    )
    const approxGap = span / Math.max(1, Math.floor(rows.length / Math.max(1, xAxisTickInterval + 1)))
    return Math.max(24, Math.min(120, approxGap / 2))
  }
  return 40
}
/**
 * Enhanced color generation utility with maximum visual distinction
 *
 * This utility provides highly distinct colors for time series visualization,
 * starting with brand colors but ensuring excellent visual separation for 20+ series.
 *
 * Features:
 * - Starts with core brand colors for consistency
 * - Uses perceptually uniform color distribution
 * - Ensures minimum visual distance between colors
 * - Optimized specifically for large numbers of series (20+)
 * - Maintains accessibility and contrast requirements
 */
const generateSeriesColors = () => {
  // Core brand colors - carefully selected for maximum distinction
  const coreColors = [
    "#39aed2", // Main brand blue
    "#50c878", // Brand green
    "#d2042d", // Brand red
    "#f0986f", // Brand orange
    "#a170d9", // Brand purple
    "#8b5a00", // Gold/yellow
  ]

  // High-contrast colors distributed across the color wheel for maximum distinction
  const highContrastColors = [
    "#3182ce", // Dark blue
    "#dc2626", // Bright red
    "#059669", // Emerald green
    "#7c3aed", // Violet
    "#ea580c", // Orange red
    "#0891b2", // Teal
    "#be185d", // Pink
    "#65a30d", // Lime
    "#4338ca", // Indigo
    "#c2410c", // Dark orange
    "#0d9488", // Dark teal
    "#7e22ce", // Dark violet
    "#ca8a04", // Amber
    "#be123c", // Rose
    "#166534", // Dark green
    "#581c87", // Dark purple
    "#92400e", // Brown
    "#1e3a8a", // Navy
    "#991b1b", // Dark red
    "#365314", // Olive
    "#78350f", // Dark brown
    "#312e81", // Deep indigo
  ]

  // Combine core and high-contrast colors
  const predefinedColors = [...coreColors, ...highContrastColors]

  // Function to convert hex to HSL for better color manipulation
  const hexToHsl = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255
    const g = parseInt(hex.slice(3, 5), 16) / 255
    const b = parseInt(hex.slice(5, 7), 16) / 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h = 0,
      s = 0,
      l = (max + min) / 2

    if (max !== min) {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0)
          break
        case g:
          h = (b - r) / d + 2
          break
        case b:
          h = (r - g) / d + 4
          break
      }
      h /= 6
    }

    return [h * 360, s * 100, l * 100]
  }

  // Generate additional colors with maximum perceptual distance
  const generateDistinctColor = (index: number, _total: number, existingColors: string[]) => {
    // Use a more sophisticated distribution for high distinction
    const goldenRatio = 0.618033988749

    // Create a hue that's maximally distant from existing colors
    const baseHue = (index * goldenRatio * 360) % 360

    // Calculate minimum distance from existing colors
    const existingHues = existingColors.map((color) => {
      const [h] = hexToHsl(color)
      return h
    })

    let finalHue = baseHue
    let maxMinDistance = 0

    // Try different hue adjustments to find the one with maximum minimum distance
    for (let adjustment = 0; adjustment < 360; adjustment += 15) {
      const testHue = (baseHue + adjustment) % 360
      const minDistance = Math.min(
        ...existingHues.map((existingHue) => {
          const distance = Math.min(Math.abs(testHue - existingHue), 360 - Math.abs(testHue - existingHue))
          return distance
        })
      )

      if (minDistance > maxMinDistance) {
        maxMinDistance = minDistance
        finalHue = testHue
      }
    }

    // Vary saturation and lightness for additional distinction
    const saturationLevels = [75, 85, 65, 95, 55] // High saturation for distinction
    const lightnessLevels = [45, 35, 55, 25, 65] // Varied lightness

    const saturation = saturationLevels[index % saturationLevels.length]
    const lightness = lightnessLevels[index % lightnessLevels.length]

    return `hsl(${finalHue}, ${saturation}%, ${lightness}%)`
  }

  return (index: number, total: number = 1) => {
    if (index < predefinedColors.length) {
      return predefinedColors[index]
    }

    // For indices beyond predefined colors, generate maximally distinct colors
    const usedColors = predefinedColors.slice(0, Math.min(index, predefinedColors.length))
    return generateDistinctColor(index - predefinedColors.length, total, usedColors)
  }
}

// Create the color generator function
const defaultColorGenerator = generateSeriesColors()

/**
 * Get a series color, optionally using a category-specific palette
 * @param index - The series index
 * @param total - Total number of series (optional)
 * @param category - Optional graph category for category-specific colors
 */
export const getSeriesColor = (
  index: number,
  total: number = 1,
  category?: GraphCategory
): string => {
  if (category && category !== "default") {
    return getCategoryColor(category, index)
  }
  return defaultColorGenerator(index, total)
}

/**
 * Generate a peak color by interpolating between the asset color and grey
 * Each peak color will be unique based on the asset index
 */
export const generatePeakColor = (assetColor: string, assetIndex: number, totalAssets: number): string => {
  const targetColor = "#f0f1f3" // Grey target color

  // Convert hex to RGB
  const hexToRgb = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return [r, g, b]
  }

  // Convert RGB to hex
  const rgbToHex = (r: number, g: number, b: number) => {
    return (
      "#" +
      [r, g, b]
        .map((x) => {
          const hex = Math.round(x).toString(16)
          return hex.length === 1 ? "0" + hex : hex
        })
        .join("")
    )
  }

  // Interpolate between two colors
  const interpolateColor = (color1: [number, number, number], color2: [number, number, number], factor: number) => {
    return [
      color1[0] + (color2[0] - color1[0]) * factor,
      color1[1] + (color2[1] - color1[1]) * factor,
      color1[2] + (color2[2] - color1[2]) * factor,
    ]
  }

  try {
    const assetRgb = hexToRgb(assetColor)
    const targetRgb = hexToRgb(targetColor)

    // Create unique interpolation factor for each asset
    // Use a varying factor between 0.3 and 0.8 to ensure uniqueness
    const baseFactor = 0.3 + 0.5 * (assetIndex / Math.max(totalAssets - 1, 1))
    // Add some variation to make each peak color more distinct
    const variation = (assetIndex % 3) * 0.1
    const interpolationFactor = Math.min(0.8, Math.max(0.3, baseFactor + variation))

    const interpolatedRgb = interpolateColor(
      assetRgb as [number, number, number],
      targetRgb as [number, number, number],
      interpolationFactor
    )
    return rgbToHex(interpolatedRgb[0], interpolatedRgb[1], interpolatedRgb[2])
  } catch (error) {
    // Fallback to a variation of the target color if parsing fails
    return `hsl(270, 60%, ${50 + ((assetIndex * 10) % 30)}%)`
  }
}

// Helper function to format values with proper decimal places
const formatValueWithDecimals = (value: number): string => {
  if (value === 0) return "0"
  if (Math.abs(value) >= 1_000_000) {
    // Millions: show as compact (e.g. 2M, 1.5M)
    return value.toLocaleString(undefined, {
      notation: "compact",
      maximumFractionDigits: 1,
    })
  }
  if (Math.abs(value) < 1) {
    // For values less than 1, use up to 4 decimal places
    return value.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")
  }
  // For values >= 1, use standard formatting (up to 2 decimal places)
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

/**
 * Round a Y-axis max value up to the nearest "nice" interval.
 * Produces clean tick labels (e.g. 0, 200, 400, 600, 800 instead of 0, 153, 306, 459, 612, 765).
 * Works for values from fractions through millions.
 */
export const niceYAxisMax = (value: number): number => {
  if (value <= 0) return 0
  // Determine order of magnitude
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
  const normalized = value / magnitude // between 1 and 10

  // Pick the nearest nice ceiling — these produce even tick divisions
  let niceNormalized: number
  if (normalized <= 1) niceNormalized = 1
  else if (normalized <= 2) niceNormalized = 2
  else if (normalized <= 2.5) niceNormalized = 2.5
  else if (normalized <= 3) niceNormalized = 3
  else if (normalized <= 4) niceNormalized = 4
  else if (normalized <= 5) niceNormalized = 5
  else if (normalized <= 6) niceNormalized = 6
  else if (normalized <= 8) niceNormalized = 8
  else niceNormalized = 10

  return niceNormalized * magnitude
}

/**
 * Round a Y-axis min value down to the nearest "nice" interval (for negative values).
 */
export const niceYAxisMin = (value: number): number => {
  if (value >= 0) return 0
  return -niceYAxisMax(Math.abs(value))
}

// Custom Tooltip Component that replicates filter styling
interface CustomTooltipProps {
  active?: boolean
  payload?: any[]
  label?: string
  series: TimeSeriesSeries<any>[]
  timeAxisFormat: Intl.DateTimeFormatOptions
  valueAxisFormat: Intl.NumberFormatOptions
  formatNumber: (options: Intl.NumberFormatOptions) => (value: number) => string
  data?: TimeSeriesDataPoint[]
  /** Label for the aggregated total row in the tooltip (defaults to "Total Peak") */
  totalLabel?: string
  /** Unit suffix shown next to values (e.g. "kW", "kg", "NL/hour") */
  unit?: string
}

const CustomTooltipContent = ({
  active,
  payload,
  label,
  series,
  timeAxisFormat,
  valueAxisFormat,
  formatNumber,
  data,
  totalLabel,
  unit,
}: CustomTooltipProps) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const labelMs = typeof label === "number" ? label : new Date(String(label || "")).getTime()
  const date = new Date(Number.isFinite(labelMs) ? labelMs : String(label || ""))
  const formattedDate = date.toLocaleString([], {
    timeZone: IANATimeZone(),
    ...timeAxisFormat,
    weekday: "short",
    month: "short",
    day: "numeric",
  })

  // Build a legend-order index so tooltip items match the legend sequence
  const legendOrder = new Map<string, number>()
  series.forEach((s, i) => legendOrder.set(s.name, i))

  const validPayload = payload
    .filter((item) => {
      const value = typeof item.value === "number" ? item.value : parseFloat(String(item.value))
      return !isNaN(value) && item.value !== null && item.value !== undefined
    })
    .sort((a, b) => {
      const aIdx = legendOrder.get(a.name) ?? Number.MAX_SAFE_INTEGER
      const bIdx = legendOrder.get(b.name) ?? Number.MAX_SAFE_INTEGER
      return aIdx - bIdx
    })

  if (validPayload.length === 0) {
    return null
  }

  const dataPoint = data?.find((d) => {
    if (typeof label === "number") return (d as { timeMs?: number }).timeMs === label
    return d.timestamp === label
  })

  return (
    <Box
      backgroundColor="card"
      borderWidth="1px"
      borderColor="border"
      borderRadius="md"
      padding={3}
      boxShadow="lg"
      minW="200px"
      zIndex="tooltip"
      position="relative"
    >
      <Text fontSize="sm" fontWeight="bold" color="primary" mb={2}>
        {formattedDate}
      </Text>

      {/* Total peak value across all assets */}
      {(() => {
        const totalPeak = validPayload.reduce((sum, entry) => {
          const isOverlay = String(entry.name).startsWith("overlay_")
          if (isOverlay) return sum
          const peakKey = `${entry.name}_peak`
          const peakValue = dataPoint?.[peakKey]
          if (typeof peakValue === "number" && Number.isFinite(peakValue)) {
            return sum + peakValue
          }
          return sum
        }, 0)
        const totalValue = validPayload.reduce((sum, entry) => {
          const isOverlay = String(entry.name).startsWith("overlay_")
          if (isOverlay) return sum
          const numericValue = typeof entry.value === "number" ? entry.value : parseFloat(String(entry.value))
          return sum + (isNaN(numericValue) ? 0 : numericValue)
        }, 0)
        if (totalPeak > 0 && totalPeak > totalValue) {
          return (
            <Flex mb={2} pb={2} borderBottomWidth="1px" borderColor="border" direction="column" gap={1}>
              <Flex>
                <Text fontSize="sm" fontWeight="bold" flex="1">
                  {totalLabel || "Total"}
                </Text>
                <Text fontSize="sm" fontWeight="bold">
                  {formatValueWithDecimals(totalValue)}{unit ? ` ${unit}` : ""}
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <Badge backgroundColor="#a170d9" color="white" fontSize="xs" minW="12px" textAlign="center">
                  ▲
                </Badge>
                <Text fontSize="xs" color="fg.muted" flex="1">
                  Total {totalLabel || "Peak"}
                </Text>
                <Text fontSize="xs" fontWeight="semibold" color="primary">
                  {formatValueWithDecimals(totalPeak)}{unit ? ` ${unit}` : ""}
                </Text>
              </Flex>
            </Flex>
          )
        }
        // Show total row even without peaks when there are multiple non-overlay series
        const nonOverlayPayload = validPayload.filter((entry) => !String(entry.name).startsWith("overlay_"))
        if (nonOverlayPayload.length > 1) {
          return (
            <Flex mb={2} pb={2} borderBottomWidth="1px" borderColor="border">
              <Text fontSize="sm" fontWeight="bold" flex="1">
                {totalLabel || "Total"}
              </Text>
              <Text fontSize="sm" fontWeight="bold">
                {formatValueWithDecimals(totalValue)}{unit ? ` ${unit}` : ""}
              </Text>
            </Flex>
          )
        }
        return null
      })()}

      <Flex direction="column" gap={1}>
        {(() => {
          const nonOverlayPayload = validPayload.filter((entry) => !String(entry.name).startsWith("overlay_"))
          const total = nonOverlayPayload.reduce((sum, entry) => {
            const v = typeof entry.value === "number" ? entry.value : parseFloat(String(entry.value))
            return sum + (isNaN(v) ? 0 : v)
          }, 0)
          const showPercentage = nonOverlayPayload.length > 1 && total > 0

          return validPayload.map((entry, index) => {
            const seriesConfig = series.find((s) => s.name === entry.name)
            const displayLabel = seriesConfig?.label || String(entry.name)
            const numericValue = typeof entry.value === "number" ? entry.value : parseFloat(String(entry.value))
            const formattedValue = !isNaN(numericValue) ? formatValueWithDecimals(numericValue) : "n/a"
            const isOverlay = String(entry.name).startsWith("overlay_")
            const percentage = showPercentage && !isOverlay && !isNaN(numericValue)
              ? ((numericValue / total) * 100).toFixed(1)
              : null
            // Fill percentage: value / series capacity when available
            const fillPct = seriesConfig?.capacity && !isNaN(numericValue) && numericValue >= 0
              ? Math.round((numericValue / seriesConfig.capacity) * 100)
              : null

            return (
              <Flex key={entry.name || index} direction="column" gap={1}>
                <Flex alignItems="center" gap={2}>
                  <Badge backgroundColor={entry.color || "#999"} color="white" fontSize="xs" minW="12px" textAlign="center">
                    ●
                  </Badge>
                  <Text fontSize="sm" color="primary" flex="1">
                    {displayLabel}
                  </Text>
                  {fillPct != null && (
                    <Text fontSize="sm" color="blue.600" fontWeight="medium">
                      {fillPct}%
                    </Text>
                  )}
                  {percentage && !fillPct && (
                    <Text fontSize="sm" color="gray.500">
                      {percentage}%
                    </Text>
                  )}
                  <Text fontSize="sm" fontWeight="semibold" color="primary">
                    {formattedValue}{seriesConfig?.capacity ? ` / ${formatValueWithDecimals(seriesConfig.capacity)}` : ""}{unit ? ` ${unit}` : ""}
                  </Text>
                </Flex>
              </Flex>
            )
          })
        })()}
      </Flex>
    </Box>
  )
}

// Custom Legend Component with hover functionality
interface CustomLegendProps {
  payload?: any[]
  onSeriesHover: (seriesName: string | null) => void
  hoveredSeries: string | null
  seriesLabelMap: Map<string, string> // Map from series name to display label
}

const CustomLegend = ({ payload, onSeriesHover: _onSeriesHover, hoveredSeries: _hoveredSeries, seriesLabelMap }: CustomLegendProps) => {
  if (!payload || !payload.length) return null

  return (
    <Flex direction="row" gap={4} justifyContent="center" alignItems="center" mb={2} flexWrap="wrap">
      {payload.map((entry, index) => {
        const seriesKey = entry.value // This is the series identifier for hover logic
        const displayLabel = seriesLabelMap.get(seriesKey) || seriesKey // Get human-readable label

        return (
          <Flex
            key={`legend-${index}`}
            data-legend-item
            alignItems="center"
            gap={1}
            fontSize="sm"
          >
            <Box
              width="12px"
              height="2px"
              backgroundColor={entry.color}
              borderStyle={entry.payload?.strokeDasharray ? "dashed" : "solid"}
              borderWidth={entry.payload?.strokeDasharray ? "1px" : "0"}
              borderColor={entry.color}
            />
            <Text color="primary">{displayLabel}</Text>
          </Flex>
        )
      })}
    </Flex>
  )
}

/** Numeric X for Recharts `scale="time"` (UTC milliseconds). */
export function appendTimeMs<T extends { timestamp?: string }>(points: T[]): (T & { timeMs: number })[] {
  return points.map((p) => ({
    ...p,
    timeMs: new Date(p.timestamp as string).getTime(),
  }))
}

/** ReferenceArea x1/x2 when X-axis uses time-linear ms domain. */
export function chartHighlightToMsRange(range: { start: string; end: string }): { x1: number; x2: number } {
  return {
    x1: new Date(range.start).getTime(),
    x2: new Date(range.end).getTime(),
  }
}

/**
 * Evenly spaced UTC ms positions across the chart window, snapped to clean
 * time boundaries (midnight, 6 h, 1 h, etc.) so tick labels read naturally.
 */
export function buildLinearTimeAxisTicksMs(startDate: DateTime, endDate: DateTime, tickCount = 8): number[] {
  const startMs = toUserTZ(startDate).unixUtcMillis()
  const endMs = toUserTZ(endDate).unixUtcMillis()
  const span = endMs - startMs
  if (span <= 0 || tickCount < 2) {
    return [startMs, endMs]
  }

  // Pick a "nice" step that produces roughly `tickCount` ticks.
  // Candidate steps from fine to coarse (in ms):
  const MS_MIN = 60_000
  const MS_HOUR = 3_600_000
  const MS_DAY = 86_400_000
  const FIVE_MIN = 5 * MS_MIN
  const niceSteps = [
    5 * MS_MIN,       // 5 min
    10 * MS_MIN,      // 10 min
    15 * MS_MIN,      // 15 min
    30 * MS_MIN,      // 30 min
    1 * MS_HOUR,      // 1 h
    2 * MS_HOUR,      // 2 h
    3 * MS_HOUR,      // 3 h
    4 * MS_HOUR,      // 4 h
    6 * MS_HOUR,      // 6 h
    8 * MS_HOUR,      // 8 h
    12 * MS_HOUR,     // 12 h
    1 * MS_DAY,       // 1 d
    2 * MS_DAY,       // 2 d
    7 * MS_DAY,       // 1 w
    14 * MS_DAY,      // 2 w
    30 * MS_DAY,      // ~1 mo
    60 * MS_DAY,      // ~2 mo
    90 * MS_DAY,      // ~3 mo
  ]

  const rawStep = span / (tickCount - 1)
  // Pick the smallest nice step that is ≥ rawStep
  const step = niceSteps.find((s) => s >= rawStep) ?? niceSteps[niceSteps.length - 1]

  // Snap start/end to the nearest 5-minute boundary so tick labels are always clean
  const snappedStart = Math.floor(startMs / FIVE_MIN) * FIVE_MIN
  const snappedEnd = Math.floor(endMs / FIVE_MIN) * FIVE_MIN

  // Snap the first interior tick to the next clean boundary after the snapped start
  const firstSnapped = Math.ceil(snappedStart / step) * step

  const ticks: number[] = [snappedStart]
  for (let t = firstSnapped; t < snappedEnd; t += step) {
    // Skip if too close to the start we already added
    if (t - snappedStart > step * 0.3) {
      ticks.push(t)
    }
  }
  ticks.push(snappedEnd)

  return ticks
}

/** Recharts category axis interval: show roughly `targetTicks` labels across the range. */
export function calculateDynamicAxisInterval(
  startDate: DateTime | undefined,
  endDate: DateTime | undefined,
  dataPointCount: number
): number {
  if (dataPointCount < 2 || !startDate || !endDate) {
    return 0
  }
  const durationMs = Math.abs(toUserTZ(endDate).unixUtcMillis() - toUserTZ(startDate).unixUtcMillis())
  const durationDays = durationMs / (24 * 60 * 60 * 1000)
  let targetTicks = 30
  if (durationDays > 90) targetTicks = 10
  else if (durationDays > 30) targetTicks = 15
  else if (durationDays > 7) targetTicks = 20
  const step = Math.max(1, Math.floor(dataPointCount / targetTicks))
  return Math.max(0, step - 1)
}

function resolutionToMs(quantity: number, resolution: string): number {
  return (
    quantity *
    (() => {
      switch (resolution) {
        case "second": {
          return 1000
        }
        case "minute": {
          return 60 * 1000
        }
        case "hour": {
          return 60 * 60 * 1000
        }
        case "day": {
          return 24 * 60 * 60 * 1000
        }
        default: {
          return 60 * 60 * 1000
        }
      }
    })()
  )
}

function alignToIntervalBoundary(timestamp: number, intervalMs: number, quantity: number, resolution: string): number {
  const date = new Date(timestamp)

  switch (resolution) {
    case "minute": {
      const totalMinutes = date.getHours() * 60 + date.getMinutes()
      const alignedMinutes = Math.floor(totalMinutes / quantity) * quantity
      const alignedDate = new Date(date)
      alignedDate.setHours(Math.floor(alignedMinutes / 60), alignedMinutes % 60, 0, 0)
      return alignedDate.getTime()
    }

    case "hour": {
      const alignedHours = Math.floor(date.getHours() / quantity) * quantity
      const hourAlignedDate = new Date(date)
      hourAlignedDate.setHours(alignedHours, 0, 0, 0)
      return hourAlignedDate.getTime()
    }

    case "day": {
      const dayAlignedDate = new Date(date)
      dayAlignedDate.setHours(0, 0, 0, 0)
      return dayAlignedDate.getTime()
    }

    default: {
      return Math.floor(timestamp / intervalMs) * intervalMs
    }
  }
}

/** When `timeseries` has no scale (e.g. alert popover), pad using this interval so the x-axis spans the full range evenly. */
export type TimeseriesPadFallbackScale = { quantity: number; resolution: "second" | "minute" | "hour" | "day" }

/**
 * Align main series to a shared timestamp grid (e.g. batch monitoring / alert graphs) so every bucket exists.
 */
export function backfillTimeSeriesDataPoints(
  data: TimeSeriesDataPoint[] | undefined,
  sharedTimestamps: string[] | undefined
): TimeSeriesDataPoint[] | undefined {
  if (!data || !sharedTimestamps || sharedTimestamps.length === 0) return data

  const dataMap = new Map(data.map((point) => [point.timestamp as string, point]))

  const valueKeys = new Set<string>()
  for (const point of data) {
    for (const key of Object.keys(point)) {
      if (key === "timestamp" || key === "time") continue
      valueKeys.add(key)
    }
  }

  return sharedTimestamps.map((timestamp) => {
    const existing = dataMap.get(timestamp)
    if (existing) return existing

    const nullPoint: TimeSeriesDataPoint = { timestamp } as TimeSeriesDataPoint
    for (const key of valueKeys) {
      nullPoint[key] = null
    }
    return nullPoint
  })
}

function padTimeseriesData(
  points: TimeSeriesDataPoint[],
  seriesList: ReadonlyArray<{ name: string }>,
  startDate: DateTime,
  endDate: DateTime,
  timeseries: Timeseries | undefined,
  /** Used when `timeseries.scale` is missing (e.g. alert "See Data" charts using 5m API granularity). */
  padFallbackScale?: TimeseriesPadFallbackScale
): TimeSeriesDataPoint[] {
  const scaleNormal = timeseries?.scale?.normal ?? padFallbackScale
  if (points.length === 0 || !scaleNormal) {
    return points
  }
  const { quantity, resolution } = scaleNormal
  const intervalMs = resolutionToMs(quantity, resolution)

  const startTimestamp = toUserTZ(startDate).unixUtcMillis()
  const endTimestamp = toUserTZ(endDate).unixUtcMillis()
  const firstDataTimestamp = new Date(points[0].timestamp).getTime()
  const lastDataTimestamp = new Date(points[points.length - 1].timestamp).getTime()

  const alignedStartTimestamp = alignToIntervalBoundary(startTimestamp, intervalMs, quantity, resolution)

  const startPoints: TimeSeriesDataPoint[] = []
  for (let timestamp = alignedStartTimestamp; timestamp < firstDataTimestamp; timestamp += intervalMs) {
    const dateTime = toUserTZ(new Date(timestamp).toISOString())
    startPoints.push({
      timestamp: dateTime.toIsoString(),
      time: dateTime,
      ...Object.fromEntries(seriesList.map((s) => [s.name, null])),
    })
  }

  const endPoints: TimeSeriesDataPoint[] = []
  let nextTimestamp = alignToIntervalBoundary(lastDataTimestamp + intervalMs, intervalMs, quantity, resolution)
  while (nextTimestamp <= endTimestamp) {
    const dateTime = toUserTZ(new Date(nextTimestamp).toISOString())
    endPoints.push({
      timestamp: dateTime.toIsoString(),
      time: dateTime,
      ...Object.fromEntries(seriesList.map((s) => [s.name, null])),
    })
    nextTimestamp += intervalMs
  }

  const combined = [...startPoints, ...points, ...endPoints]
  combined.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
  return combined
}

// Enhanced type definitions for better type safety
export type TimeSeriesDataPoint = {
  timestamp: string
  time?: DateTime
} & Record<string, number | null | string | DateTime>

export type TimeSeriesSeries<T extends readonly string[]> = {
  name: T[number]
  color: string
  label: string
  group?: string // New optional group identifier
  /** Owning asset id; set by the systems monitoring hook so single-asset selection can group series. */
  assetId?: string
  /** Owning asset's display name; used to label a selection that spans several series. */
  assetName?: string
  strokeDasharray?: string
  strokeWidth?: number
  dot?: boolean
  connectNulls?: boolean
  /** Per-series capacity (m³) for fill percentage calculation in tooltip. */
  capacity?: number | null
  type?:
  | "basis"
  | "basisClosed"
  | "basisOpen"
  | "linear"
  | "linearClosed"
  | "monotoneX"
  | "monotoneY"
  | "monotone"
  | "natural"
  | "step"
  | "stepBefore"
  | "stepAfter"
}

// Asset configuration for custom series
export interface AssetConfig {
  assetId: string
  /** When set, this config applies only to this asset+connection pair (Load / multi-feed series). */
  connection?: string
  color?: string
  label: string
  group?: string // Optional group identifier for grouping assets (e.g., by location)
  strokeWidth?: number
  strokeDasharray?: string
  dot?: boolean
  connectNulls?: boolean
  showPeaks?: boolean // Per-asset peak control - defaults to false (disabled)
}

/** A time range to highlight on the chart (e.g. alert event period) */
export interface ChartHighlightRange {
  start: string
  end: string
  label?: string
  color?: string
}

interface TimeSeriesProps<T extends readonly string[]> {
  data: TimeSeriesDataPoint[]
  series: TimeSeriesSeries<T>[]
  graph?: Graph
  timeseries?: Timeseries
  endDate?: DateTime
  startDate?: DateTime
  comparison?: { label: string; data: any[] }
  height?: string | number
  onDataPoint?: (points: any[]) => void
  onLoaded?: () => void
  showTooltip?: boolean
  showLegend?: boolean
  showGrid?: boolean
  timeAxisFormat?: Intl.DateTimeFormatOptions
  valueAxisFormat?: Intl.NumberFormatOptions
  assetConfigs?: AssetConfig[] // Custom asset configurations from system
  /** Hide the asset/series selector menu (e.g. system aggregated Timescale view) */
  hideAssetMenu?: boolean
  /** Extra controls rendered in the header row (e.g. peak toggle, breakdown button) */
  headerEndContent?: ReactNode
  /** Overlay data (e.g. site load on generation). Default: secondary right Y-axis. */
  overlayData?: {
    data: TimeSeriesDataPoint[]
    series: TimeSeriesSeries<T>[]
    yAxisLabel: string
    /** When true, overlay lines use the same Y-axis as the main series (e.g. power + site load both kW). */
    useSameYAxis?: boolean
  }
  /** Time ranges to highlight on the chart (e.g. alert event periods) */
  highlightRanges?: ChartHighlightRange[]
  /** When set (e.g. from shared batch timestamps), aligns X tick density across monitoring graphs */
  xAxisTickInterval?: number
  /** Align main series to this timestamp grid before merge/pad (alert popovers, batch views). */
  sharedTimestamps?: string[]
  /** When timeseries.scale is absent, pad x-range with this interval (default 5m for alert charts). */
  padFallbackScale?: TimeseriesPadFallbackScale
  /** Explicit X tick positions (ms); when set, all graphs in a view share the same ticks (e.g. monitoring). */
  xAxisTicksMs?: number[]
  /** When true, render as stacked area chart (e.g. parent asset showing child contributions). */
  stacked?: boolean
}

const TimeSeriesGraph = <T extends readonly string[]>(props: TimeSeriesProps<T>) => {
  const {
    data,
    series,
    graph,
    timeseries,
    endDate,
    startDate,
    height = "sm",
    onDataPoint,
    onLoaded,
    showTooltip = true,
    showLegend = true,
    showGrid = true,
    timeAxisFormat = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    },
    valueAxisFormat = {
      maximumFractionDigits: 1,
    },
    hideAssetMenu = false,
    headerEndContent,
    overlayData,
    highlightRanges,
    xAxisTickInterval,
    sharedTimestamps,
    padFallbackScale,
    xAxisTicksMs,
    stacked = false,
  } = props

  // Asset selection state - track individual assets and their peak visibility
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set())
  const [assetPeakVisibility, setAssetPeakVisibility] = useState<Map<string, boolean>>(new Map())

  // Legend hover state for line highlighting
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null)

  // Get unique assets (exclude peak series)
  const uniqueAssets = useMemo(() => {
    return series.filter((s) => {
      // Exclude multi-asset peak series (end with "_peaks")
      if (s.name.endsWith("_peaks")) return false

      // Exclude single-asset peak series (common peak names)
      const lowerName = s.name.toLowerCase()
      if (lowerName === "peaks" || lowerName === "comparison" || lowerName === "peak") return false

      return true
    })
  }, [series])

  // Derive groups from unique assets - assets without group go to "Ungrouped"
  const groups = useMemo(() => {
    const groupMap = new Map<string, TimeSeriesSeries<T>[]>()

    uniqueAssets.forEach((s) => {
      const groupName = s.group || "Ungrouped"
      if (!groupMap.has(groupName)) {
        groupMap.set(groupName, [])
      }
      groupMap.get(groupName)!.push(s)
    })

    return groupMap
  }, [uniqueAssets])

  // Initialise selected assets and peak visibility when series change
  useEffect(() => {
    if (uniqueAssets.length > 0) {
      setSelectedAssets(new Set(uniqueAssets.map((s) => s.name)))

      // Initialise peak visibility - check if peak series exist for each asset
      const newPeakVisibility = new Map<string, boolean>()
      uniqueAssets.forEach((asset) => {
        // Check for multi-asset peak series or single-asset peak series
        const hasMultiAssetPeak = series.some((s) => s.name === `${asset.name}_peaks`)
        const hasSingleAssetPeak = uniqueAssets.length === 1 && series.some((s) => isPeakSeries(s.name))
        const hasPeakSeries = hasMultiAssetPeak || hasSingleAssetPeak

        newPeakVisibility.set(asset.name, false) // Default to false (disabled)
      })
      setAssetPeakVisibility(newPeakVisibility)
    }
  }, [uniqueAssets, series])

  // Derived group states based on asset selection
  const groupStates = useMemo(() => {
    const states = new Map<
      string,
      { selected: number; total: number; isFullySelected: boolean; isPartiallySelected: boolean }
    >()

    groups.forEach((groupAssets, groupName) => {
      const selectedCount = groupAssets.filter((asset) => selectedAssets.has(asset.name)).length
      const total = groupAssets.length
      states.set(groupName, {
        selected: selectedCount,
        total,
        isFullySelected: selectedCount === total,
        isPartiallySelected: selectedCount > 0 && selectedCount < total,
      })
    })

    return states
  }, [groups, selectedAssets])

  // Helper function to check if a series is a peak series
  const isPeakSeries = (seriesName: string) => {
    if (seriesName.endsWith("_peaks")) return true
    const lowerName = seriesName.toLowerCase()
    return lowerName === "peaks" || lowerName === "comparison" || lowerName === "peak"
  }

  // Helper function to get the asset name from a peak series
  const getAssetNameFromPeakSeries = (seriesName: string) => {
    if (seriesName.endsWith("_peaks")) {
      return seriesName.replace("_peaks", "")
    }
    // For single-asset scenarios, find the first (and only) unique asset
    return uniqueAssets[0]?.name || ""
  }

  // Filter series and data based on selected assets and peak visibility
  const filteredSeries = series.filter((s) => {
    if (isPeakSeries(s.name)) {
      // For peak series, check both asset selection and peak visibility
      const assetName = getAssetNameFromPeakSeries(s.name)
      return selectedAssets.has(assetName) && assetPeakVisibility.get(assetName) === true
    } else {
      // For regular asset series, just check asset selection
      return selectedAssets.has(s.name)
    }
  })

  const filteredData = data.map((point) => {
    const filteredPoint = { ...point }
    series.forEach((s) => {
      if (isPeakSeries(s.name)) {
        // For peak series, check both asset selection and peak visibility
        const assetName = getAssetNameFromPeakSeries(s.name)
        if (!selectedAssets.has(assetName) || assetPeakVisibility.get(assetName) !== true) {
          delete filteredPoint[s.name]
        }
      } else {
        // For regular asset series, just check asset selection
        if (!selectedAssets.has(s.name)) {
          delete filteredPoint[s.name]
        }
      }
    })
    return filteredPoint
  })

  // Handle individual asset toggle with validation (must keep at least one asset selected)
  const handleAssetToggle = (assetName: string, isSelected: boolean) => {
    setSelectedAssets((prev) => {
      const newSet = new Set(prev)
      if (isSelected) {
        newSet.add(assetName)
      } else {
        // Only allow deselection if more than one asset is currently selected
        if (prev.size > 1) {
          newSet.delete(assetName)
        }
      }
      return newSet
    })
  }

  // Handle individual peak toggle for a specific asset
  const handleAssetPeakToggle = (assetName: string, showPeaks: boolean) => {
    setAssetPeakVisibility((prev) => {
      const newMap = new Map(prev)
      newMap.set(assetName, showPeaks)
      return newMap
    })
  }

  // Handle select all assets
  const handleSelectAllAssets = () => {
    const allSelected = selectedAssets.size === uniqueAssets.length
    if (allSelected) {
      // Deselect all but keep at least one
      const firstAsset = uniqueAssets[0]?.name
      if (firstAsset) {
        setSelectedAssets(new Set([firstAsset]))
      }
    } else {
      // Select all assets
      setSelectedAssets(new Set(uniqueAssets.map((s) => s.name)))
    }
  }

  // Handle toggle all peaks
  const handleToggleAllPeaks = () => {
    const allPeaksEnabled = Array.from(assetPeakVisibility.values()).every((value) => value === true)
    setAssetPeakVisibility((prev) => {
      const newMap = new Map(prev)
      // Toggle all peaks to opposite of current state
      uniqueAssets.forEach((asset) => {
        // Check for both multi-asset and single-asset peak data
        const hasMultiAssetPeak = series.some((s) => s.name === `${asset.name}_peaks`)
        const hasSingleAssetPeak = uniqueAssets.length === 1 && series.some((s) => isPeakSeries(s.name))
        const hasPeakData = hasMultiAssetPeak || hasSingleAssetPeak

        if (hasPeakData) {
          newMap.set(asset.name, !allPeaksEnabled)
        }
      })
      return newMap
    })
  }

  // Handle group toggle - select/deselect all assets in the group
  const handleGroupToggle = (groupName: string, isSelected: boolean) => {
    const groupAssets = groups.get(groupName) || []
    const groupAssetNames = groupAssets.map((asset) => asset.name)

    setSelectedAssets((prev) => {
      const newSet = new Set(prev)

      if (isSelected) {
        // Add all assets in the group
        groupAssetNames.forEach((name) => newSet.add(name))
      } else {
        // Remove assets in the group, but ensure at least one asset remains selected overall
        const assetsToRemove = groupAssetNames.filter((name) => newSet.has(name))

        // Only allow deselection if we'll still have at least one asset selected
        if (newSet.size - assetsToRemove.length > 0) {
          assetsToRemove.forEach((name) => newSet.delete(name))
        }
      }

      return newSet
    })
  }

  const useSameYAxis = overlayData?.useSameYAxis === true

  const processedData = useMemo(() => {
    const baseData = sharedTimestamps?.length
      ? backfillTimeSeriesDataPoints(filteredData, sharedTimestamps) ?? filteredData
      : filteredData

    const mapped = baseData.map((point) => ({
      ...point,
      timestamp: point.time ? toUserTZ(point.time).toIsoString() : point.timestamp,
    }))
    mapped.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

    // Merge overlay data if present
    if (overlayData) {
      const overlayMap = new Map(
        overlayData.data.map((point) => [
          point.time ? toUserTZ(point.time).toIsoString() : point.timestamp,
          point,
        ])
      )

      mapped.forEach((point) => {
        const overlayPoint = overlayMap.get(point.timestamp)
        if (overlayPoint) {
          const row = point as TimeSeriesDataPoint
          overlayData.series.forEach((overlaySeries) => {
            const overlayKey = `overlay_${overlaySeries.name}`
            row[overlayKey] = overlayPoint[overlaySeries.name] as number | null
          })
        }
      })
    }

    let result = mapped
    if (startDate && endDate && mapped.length > 0) {
      const allSeries = overlayData
        ? [...filteredSeries, ...overlayData.series.map(s => ({ ...s, name: `overlay_${s.name}` }))]
        : filteredSeries
      const padFallback =
        !timeseries?.scale?.normal
          ? (padFallbackScale ?? ({ quantity: 5, resolution: "minute" } as TimeseriesPadFallbackScale))
          : undefined
      result = padTimeseriesData(mapped, allSeries, startDate, endDate, timeseries, padFallback)
    }
    return appendTimeMs(result)
  }, [filteredData, filteredSeries, startDate, endDate, timeseries, overlayData, sharedTimestamps, padFallbackScale])

  const timeAxisDomain = useMemo(
    () => timescaleTimeDomain(xAxisTicksMs, startDate, endDate),
    [startDate, endDate, xAxisTicksMs]
  )
  const formatTimeAxisTick = useMemo(
    () => timescaleTickFormatter(timescaleSpanMs(startDate, endDate)),
    [startDate, endDate]
  )
  /** Prefer parent tick density hint; time scale uses minTickGap so this is a soft cap only. */
  const timeMinTickGap = useMemo(
    () => timescaleMinTickGap(xAxisTickInterval, processedData),
    [xAxisTickInterval, processedData]
  )
  const overlaySeries = useMemo(() => {
    if (!overlayData) return []
    return overlayData.series.map((s) => ({
      ...s,
      name: `overlay_${s.name}`,
      color: OVERLAY_LINE_COLOR,
      strokeDasharray: undefined,
      strokeWidth: 1,
    }))
  }, [overlayData])

  const allSeries = useMemo(() => {
    return [...filteredSeries, ...overlaySeries]
  }, [filteredSeries, overlaySeries])

  const unit = useMemo(() => {
    if (graph?.source?.desiredUnit) {
      return graph.source.desiredUnit
    }
    const yAxisMatch = graph?.yAxis?.match(/\(([^)]+)\)/)
    return yAxisMatch?.[1] || undefined
  }, [graph])

  /** Tooltip total row label — uses aggregatedValueLabel if set, otherwise the yAxis label without unit. */
  const tooltipTotalLabel = useMemo(() => {
    const aggregatedLabel = (graph as { aggregatedValueLabel?: string } | undefined)?.aggregatedValueLabel
    if (aggregatedLabel) return aggregatedLabel
    if (graph?.yAxis) {
      // Strip the unit in parentheses, e.g. "Production (NL)" → "Production"
      const withoutUnit = graph.yAxis.replace(/\s*\([^)]*\)\s*$/, "").trim()
      if (withoutUnit) return withoutUnit
    }
    return graph?.title || "Total"
  }, [graph])

  const chart = useChart({
    data: processedData,
    series: allSeries,
  } as unknown as Parameters<typeof useChart>[0])

  // Create a mapping from series names to display labels for the legend
  const seriesLabelMap = useMemo(() => {
    const map = new Map<string, string>()
    filteredSeries.forEach((s) => {
      map.set(s.name, s.label)
    })
    overlaySeries.forEach((s) => {
      map.set(s.name, s.label)
    })
    return map
  }, [filteredSeries, overlaySeries])

  // Notify parent components of data changes and loading status
  useEffect(() => {
    if (onDataPoint && processedData.length > 0) {
      onDataPoint(processedData)
    }
  }, [onDataPoint, processedData])

  useEffect(() => {
    if (onLoaded && processedData.length > 0) {
      onLoaded()
    }
  }, [onLoaded, processedData])

  // If we are on mobile, hide the legend
  const isMobile = useMemo(() => {
    return window.innerWidth < 768
  }, [])

  return (
    <Box>
      {graph && (
        <Flex mb={4} alignItems="center" justifyContent="space-between">
          <Flex alignItems="center">
            <Heading fontFamily="montserrat" size="sm">
              {graph.title}
            </Heading>

          </Flex>

          <Flex alignItems="center" gap={3}>
            {headerEndContent}

            {/* Asset Selection Dropdown - show if any assets exist */}
            {!hideAssetMenu && uniqueAssets.length > 0 && (
              <Menu.Root closeOnSelect={false}>
                <Menu.Trigger asChild>
                  <Box
                    as="button"
                    padding={2}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor="border"
                    backgroundColor="card"
                    cursor="pointer"
                    width={{ base: "200px", md: "250px" }}
                    textAlign="left"
                    fontSize="sm"
                    _hover={{ borderColor: "blue.300" }}
                  >
                    <Flex alignItems="center" justifyContent="space-between">
                      <Text truncate>
                        {selectedAssets.size === uniqueAssets.length
                          ? `All Assets (${selectedAssets.size})`
                          : `${selectedAssets.size}/${uniqueAssets.length} assets`}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        ▼
                      </Text>
                    </Flex>
                  </Box>
                </Menu.Trigger>
                <Menu.Positioner>
                  <Menu.Content width="400px" maxH="500px" overflowY="auto">
                    {/* Top bar with global controls */}
                    <Box borderBottomWidth="2px" borderColor="border" pb={2} mb={3} mx={2}>
                      <Flex alignItems="center" justifyContent="space-between" gap={4} fontSize="sm">
                        <Link variant="primary" onClick={handleSelectAllAssets} cursor="pointer">
                          Select all
                        </Link>
                        {/* Only show toggle peaks if there are any peak series available */}
                        {series.some((s) => isPeakSeries(s.name)) && (
                          <Link variant="primary" onClick={handleToggleAllPeaks} cursor="pointer">
                            Toggle peaks
                          </Link>
                        )}
                      </Flex>
                    </Box>

                    {/* Asset list grouped by groups if applicable */}
                    {Array.from(groups.entries())
                      .sort(([groupNameA], [groupNameB]) => {
                        // Always put "Ungrouped" last
                        if (groupNameA === "Ungrouped") return 1
                        if (groupNameB === "Ungrouped") return -1
                        // Sort other groups alphabetically
                        return groupNameA.localeCompare(groupNameB)
                      })
                      .map(([groupName, groupAssets]) => {
                        const groupState = groupStates.get(groupName)!
                        const isGroupFullySelected = groupState.isFullySelected
                        const isGroupPartiallySelected = groupState.isPartiallySelected

                        return (
                          <Box
                            key={groupName}
                            borderBottomWidth={groups.size > 1 ? "1px" : "0px"}
                            borderColor="border.muted"
                            pb={groups.size > 1 ? 3 : 0}
                            mb={groups.size > 1 ? 3 : 0}
                            mx={2}
                          >
                            {/* Group header - only show if multiple groups */}
                            {groups.size > 1 && (
                              <Menu.Item
                                value={`group-${groupName}`}
                                onSelect={() => handleGroupToggle(groupName, !isGroupFullySelected)}
                                cursor="pointer"
                                _hover={{ backgroundColor: "bg.subtle" }}
                                mb={2}
                                borderRadius="sm"
                              >
                                <Flex alignItems="center" gap={2} width="100%">
                                  <input
                                    type="checkbox"
                                    checked={isGroupFullySelected}
                                    ref={(el) => {
                                      if (el) el.indeterminate = isGroupPartiallySelected
                                    }}
                                    disabled={selectedAssets.size === 1 && isGroupFullySelected}
                                    onChange={() => { }} // Handled by parent onSelect
                                  />
                                  <Text
                                    fontSize="sm"
                                    fontWeight="bold"
                                    color={isGroupFullySelected || isGroupPartiallySelected ? "fg" : "gray.500"}
                                  >
                                    {groupName} ({groupState.selected}/{groupState.total})
                                  </Text>
                                </Flex>
                              </Menu.Item>
                            )}

                            {/* Individual assets with peak toggles */}
                            {groupAssets.map((asset) => {
                              const isAssetSelected = selectedAssets.has(asset.name)
                              // Check for both multi-asset and single-asset peak data
                              const hasMultiAssetPeak = series.some((s) => s.name === `${asset.name}_peaks`)
                              const hasSingleAssetPeak =
                                uniqueAssets.length === 1 && series.some((s) => isPeakSeries(s.name))
                              const hasPeakData = hasMultiAssetPeak || hasSingleAssetPeak
                              const isPeakVisible = assetPeakVisibility.get(asset.name) === true

                              return (
                                <Box key={asset.name} ml={groups.size > 1 ? 4 : 0} mb={1}>
                                  <Flex alignItems="center" justifyContent="space-between" width="100%">
                                    {/* Left side: Asset checkbox and info */}
                                    <Menu.Item
                                      value={asset.name}
                                      onSelect={() => handleAssetToggle(asset.name, !isAssetSelected)}
                                      cursor="pointer"
                                      _hover={{ backgroundColor: "bg.subtle" }}
                                      borderRadius="sm"
                                      flex="1"
                                      mr={2}
                                    >
                                      <Flex alignItems="center" gap={2}>
                                        <input
                                          type="checkbox"
                                          checked={isAssetSelected}
                                          disabled={selectedAssets.size === 1 && isAssetSelected}
                                          onChange={() => { }} // Handled by parent onSelect
                                        />
                                        <Badge
                                          backgroundColor={asset.color}
                                          color="white"
                                          fontSize="xs"
                                          minW="12px"
                                          textAlign="center"
                                          opacity={isAssetSelected ? 1 : 0.5}
                                        >
                                          ●
                                        </Badge>
                                        <Text fontSize="sm" color={isAssetSelected ? "fg" : "gray.500"}>
                                          {asset.label}
                                        </Text>
                                      </Flex>
                                    </Menu.Item>

                                    {/* Right side: Peak toggle */}
                                    {hasPeakData && (
                                      <Flex alignItems="center" gap={2}>
                                        <Text fontSize="xs" color="gray.500">
                                          Show peaks
                                        </Text>
                                        <Switch
                                          variant="small"
                                          checked={isPeakVisible}
                                          disabled={!isAssetSelected}
                                          onCheckedChange={(details) => {
                                            if (isAssetSelected) {
                                              handleAssetPeakToggle(asset.name, details.checked)
                                            }
                                          }}
                                          size="sm"
                                        />
                                      </Flex>
                                    )}
                                  </Flex>
                                </Box>
                              )
                            })}
                          </Box>
                        )
                      })}
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>
            )}
          </Flex>
        </Flex>
      )}

      <Chart.Root maxH={height} chart={chart}>
        <ResponsiveContainer width="100%" height="100%">
          {(() => {
            const ChartComponent = stacked ? AreaChart : LineChart
            return (
          <ChartComponent
            data={processedData}
            margin={{
              top: 5,
              right: timescaleChartMarginRight(Boolean(overlayData && !useSameYAxis)),
              left: 20,
              bottom: 60,
            }}
          >
            {showGrid && <CartesianGrid stroke={chart.color("border")} strokeDasharray="3 3" vertical={false} />}

            {highlightRanges?.map((range, index) => {
              const { x1, x2 } = chartHighlightToMsRange(range)
              return (
                <ReferenceArea
                  key={`highlight-${index}`}
                  yAxisId="left"
                  x1={x1}
                  x2={x2}
                  fill={range.color || "rgba(255, 165, 0, 0.2)"}
                  fillOpacity={0.3}
                  stroke={range.color || "rgba(255, 165, 0, 0.6)"}
                  strokeWidth={4}
                  label={
                    range.label
                      ? {
                        value: range.label,
                        position: "insideTop",
                        fill: "#666",
                        fontSize: 11,
                      }
                      : undefined
                  }
                />
              )
            })}

            <XAxis
              axisLine={false}
              dataKey="timeMs"
              type="number"
              scale="time"
              domain={timeAxisDomain ?? ["dataMin", "dataMax"]}
              tickFormatter={formatTimeAxisTick}
              stroke={chart.color("chart_axis")}
              fontSize={12}
              tickLine={false}
              ticks={xAxisTicksMs}
              minTickGap={xAxisTicksMs?.length ? undefined : timeMinTickGap}
              height={1}
            />

            <YAxis
              yAxisId="left"
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              stroke={chart.color("chart_axis")}
              fontSize={12}
              tickFormatter={(value: number) => formatValueWithDecimals(value)}
              allowDecimals={false}
              tickCount={6}
              label={
                graph?.yAxis
                  ? {
                    value: graph.yAxis,
                    angle: -90,
                    position: "insideLeft",
                  }
                  : undefined
              }
              domain={
                graph?.yAxisMinValue !== undefined
                  ? [(dataMin: number) => niceYAxisMin(Math.min(dataMin, graph.yAxisMinValue!)), (dataMax: number) => niceYAxisMax(dataMax)]
                  : [(dataMin: number) => niceYAxisMin(dataMin), (dataMax: number) => niceYAxisMax(dataMax)]
              }
            />

            {overlayData && !useSameYAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                width={TIMESCALE_SECONDARY_RIGHT_YAXIS_WIDTH_PX}
                axisLine={false}
                tickLine={false}
                tickMargin={10}
                stroke={chart.color("chart_axis")}
                fontSize={12}
                tickFormatter={(value: number) => formatValueWithDecimals(value)}
                allowDecimals={false}
                tickCount={6}
                domain={[(dataMin: number) => niceYAxisMin(dataMin), (dataMax: number) => niceYAxisMax(dataMax)]}
                label={{
                  value: overlayData.yAxisLabel,
                  angle: 90,
                  position: "insideRight",
                }}
              />
            )}

            {showTooltip && (
              <Tooltip
                animationDuration={0}
                wrapperStyle={{ zIndex: 1000 }}
                cursor={{ stroke: chart.color("border") }}
                content={
                  <CustomTooltipContent
                    series={allSeries}
                    timeAxisFormat={timeAxisFormat}
                    valueAxisFormat={valueAxisFormat}
                    formatNumber={chart.formatNumber}
                    data={processedData}
                    totalLabel={tooltipTotalLabel}
                    unit={unit}
                  />
                }
                itemSorter={(item) => {
                  return typeof item.value === "number" ? item.value * -1 : 0
                }}
              />
            )}

            {showLegend && !isMobile && (
              <Legend
                verticalAlign="top"
                align="center"
                content={(props) => (
                  <CustomLegend
                    {...props}
                    onSeriesHover={setHoveredSeries}
                    hoveredSeries={hoveredSeries}
                    seriesLabelMap={seriesLabelMap}
                  />
                )}
              />
            )}

            {chart.series.map((item) => {
              // Find the original series config using the filtered series (respects transformations)
              const name = String(item.name ?? "")
              const seriesConfig = allSeries.find((s) => s.name === name)
              const isOverlay = name.startsWith("overlay_")
              const isHovered = hoveredSeries === name
              const isOtherHovered = hoveredSeries && hoveredSeries !== name
              const baseStrokeWidth = isOverlay ? (seriesConfig?.strokeWidth ?? 1) : (seriesConfig?.strokeWidth ?? 2)

              return stacked ? (
                <Area
                  key={name}
                  yAxisId={isOverlay && !useSameYAxis ? "right" : "left"}
                  isAnimationActive={false}
                  dataKey={chart.key(name as never)}
                  stackId="1"
                  stroke={chart.color(item.color)}
                  fill={chart.color(item.color)}
                  fillOpacity={isOtherHovered ? 0.1 : 0.6}
                  strokeWidth={isHovered ? baseStrokeWidth + 1 : baseStrokeWidth}
                  strokeOpacity={isOtherHovered ? 0.3 : 1}
                  connectNulls={seriesConfig?.connectNulls}
                  type={seriesConfig?.type || "monotone"}
                />
              ) : (
                <Line
                  key={name}
                  yAxisId={isOverlay && !useSameYAxis ? "right" : "left"}
                  isAnimationActive={false}
                  dataKey={chart.key(name as never)}
                  strokeWidth={isHovered ? baseStrokeWidth + 1 : baseStrokeWidth}
                  stroke={chart.color(item.color)}
                  strokeOpacity={isOtherHovered ? 0.3 : 1}
                  strokeDasharray={seriesConfig?.strokeDasharray}
                  dot={seriesConfig?.dot !== false}
                  activeDot={seriesConfig?.dot !== false ? { r: isHovered ? 4 : 3 } : false}
                  connectNulls={seriesConfig?.connectNulls}
                  type={seriesConfig?.type || "monotone"}
                />
              )
            })}
          </ChartComponent>
            )
          })()}
        </ResponsiveContainer>
      </Chart.Root>
    </Box>
  )
}

export default TimeSeriesGraph
